import { z } from 'zod';
import { validation } from './errors.js';
/**
 * Invoice template.
 *
 * One structure, three consumers: the renderer draws it, Claude edits it from a
 * description, and the visual editor edits it by clicking. Because they share a
 * model, a change made one way is legible to the others.
 *
 * Deliberately a list of typed blocks rather than free layout — reordering and
 * restyling stay possible without giving anything the ability to invent a
 * layout the renderer cannot draw.
 */
/** A link, never an image: enough for any real URL, far too little for base64. */
const MAX_LOGO_URL = 2048;
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const hexColor = (fallback) => z.string().regex(HEX, 'Use a hex colour like #1b3a6b').default(fallback);
/** react-pdf's built-in faces. No files to host, and they render identically everywhere. */
export const TEMPLATE_FONTS = ['Helvetica', 'Times-Roman', 'Courier'];
export const LINE_COLUMNS = ['description', 'quantity', 'unit', 'unit_price', 'discount', 'tax', 'total'];
export const META_FIELDS = ['number', 'issued_at', 'due_at', 'job_number', 'customer_number', 'reference'];
export const TemplateTheme = z.object({
    accent: hexColor('#1b3a6b'),
    text: hexColor('#111111'),
    muted: hexColor('#6b7280'),
    border: hexColor('#e5e7eb'),
    font: z.enum(TEMPLATE_FONTS).default('Helvetica'),
    baseFontSize: z.number().min(7).max(14).default(9.5),
    density: z.enum(['compact', 'normal', 'roomy']).default('normal'),
    pageSize: z.enum(['A4', 'LETTER']).default('A4'),
    /**
     * The uploaded image used as the logo, as a document id.
     *
     * A logo is a file, not a value: it is uploaded like any other document and
     * referenced here. Carrying image bytes in the spec asks whoever is editing —
     * often a language model — to reproduce megabytes of base64 exactly, which
     * they cannot do; a single dropped character yields a file no PNG decoder
     * will open, and react-pdf drops a broken image without a word.
     */
    logoDocumentId: z.string().max(64).optional(),
    /**
     * An already-hosted logo, for a workspace that has one. Anything that is not
     * an http(s) link — a data: URI above all — is dropped rather than rejected,
     * so a spec saved before this rule still renders, just without its logo.
     */
    logoUrl: z.preprocess((v) => (typeof v === 'string' && /^https?:\/\//i.test(v) && v.length <= MAX_LOGO_URL ? v : undefined), z.string().max(MAX_LOGO_URL).optional()),
    logoWidth: z.number().min(20).max(240).default(120),
});
/**
 * Rejects a logo somebody has tried to inline instead of upload.
 *
 * Parsing quietly drops these so old rows still render; saving must not, or the
 * caller is told the change succeeded when the logo has silently vanished.
 */
export function checkLogoOnWrite(spec) {
    const theme = spec?.theme;
    const url = theme?.logoUrl;
    if (url == null || url === '')
        return;
    if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
        throw validation('A logo cannot be pasted into the template as image data.', 'Upload the image first — create_upload_url, then create_document — and call set_invoice_logo with the document id. theme.logoUrl only accepts a link to an image that is already hosted.');
    }
    if (url.length > MAX_LOGO_URL) {
        throw validation(`theme.logoUrl is ${url.length} characters, over the ${MAX_LOGO_URL} limit.`, 'It should be a link, not the image itself.');
    }
}
export const TemplateBlock = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('header'),
        logo: z.enum(['left', 'right', 'none']).default('left'),
        style: z.enum(['plain', 'banner', 'rule']).default('plain'),
        title: z.string().max(40).default('INVOICE'),
        showTitle: z.boolean().default(true),
    }),
    z.object({
        type: z.literal('meta'),
        fields: z.array(z.enum(META_FIELDS)).default(['number', 'issued_at', 'due_at']),
        align: z.enum(['left', 'right']).default('right'),
    }),
    z.object({
        type: z.literal('parties'),
        order: z.enum(['from_to', 'to_from']).default('from_to'),
        showFrom: z.boolean().default(true),
        showTo: z.boolean().default(true),
        fromLabel: z.string().max(30).default('From'),
        toLabel: z.string().max(30).default('Bill to'),
    }),
    z.object({
        type: z.literal('lines'),
        columns: z.array(z.enum(LINE_COLUMNS)).min(1).default(['description', 'quantity', 'unit_price', 'total']),
        /** Rename a column, e.g. { quantity: 'Hours' }. */
        labels: z.record(z.string(), z.string().max(24)).default({}),
        zebra: z.boolean().default(false),
        showHeader: z.boolean().default(true),
    }),
    z.object({
        type: z.literal('totals'),
        showSubtotal: z.boolean().default(true),
        showDiscount: z.boolean().default(true),
        showTax: z.boolean().default(true),
        showPaid: z.boolean().default(true),
        align: z.enum(['left', 'right']).default('right'),
    }),
    z.object({
        type: z.literal('notes'),
        source: z.enum(['invoice', 'fixed']).default('invoice'),
        text: z.string().max(2000).default(''),
        heading: z.string().max(40).default('Notes'),
    }),
    z.object({
        type: z.literal('terms'),
        text: z.string().max(2000).default(''),
        heading: z.string().max(40).default('Terms'),
    }),
    z.object({
        type: z.literal('custom_fields'),
        entity: z.enum(['invoices', 'customers', 'jobs']).default('invoices'),
        /** Empty means every field defined for that record. */
        keys: z.array(z.string()).default([]),
        heading: z.string().max(40).default('Details'),
    }),
    z.object({ type: z.literal('spacer'), height: z.number().min(2).max(120).default(16) }),
    z.object({
        type: z.literal('footer'),
        text: z.string().max(500).default(''),
        repeatOnEveryPage: z.boolean().default(true),
        showPageNumbers: z.boolean().default(true),
    }),
]);
export const InvoiceTemplateSpec = z.object({
    theme: TemplateTheme.default(() => TemplateTheme.parse({})),
    blocks: z.array(TemplateBlock).min(1).max(30),
});
/** What a workspace gets before anyone customises anything. */
export const DEFAULT_INVOICE_TEMPLATE = {
    theme: {},
    blocks: [
        { type: 'header', logo: 'left', style: 'plain', title: 'INVOICE', showTitle: true },
        { type: 'meta', fields: ['number', 'issued_at', 'due_at'], align: 'right' },
        { type: 'parties', order: 'from_to', showFrom: true, showTo: true },
        { type: 'lines', columns: ['description', 'quantity', 'unit_price', 'total'], labels: {}, zebra: false, showHeader: true },
        { type: 'totals', showSubtotal: true, showDiscount: true, showTax: true, showPaid: true, align: 'right' },
        { type: 'notes', source: 'invoice', text: '', heading: 'Notes' },
        { type: 'footer', text: '', repeatOnEveryPage: true, showPageNumbers: true },
    ],
};
export function parseInvoiceTemplate(value) {
    return InvoiceTemplateSpec.parse(value ?? DEFAULT_INVOICE_TEMPLATE);
}
/** Guidance embedded in the tool description so an agent edits the spec correctly. */
export const INVOICE_TEMPLATE_GUIDE = `
INVOICE TEMPLATE
  { "theme": {...}, "blocks": [ ... ] }   blocks render top to bottom, in order.

  theme: accent/text/muted/border (hex), font (Helvetica | Times-Roman | Courier),
         baseFontSize 7-14, density compact|normal|roomy, pageSize A4|LETTER,
         logoWidth 20-240, logoUrl (an https link to an image hosted elsewhere)

  THE LOGO IS A FILE, NOT A SETTING. Never put image data in this spec: upload
  the image (create_upload_url, then create_document) and call set_invoice_logo
  with the document id. Base64 pasted here is rejected, and a logo typed out by
  hand loses characters and silently fails to render.

  blocks:
    header        {logo: left|right|none, style: plain|banner|rule, title, showTitle}
    meta          {fields: [number, issued_at, due_at, job_number, customer_number, reference], align}
    parties       {order: from_to|to_from, showFrom, showTo, fromLabel, toLabel}
    lines         {columns: [description, quantity, unit, unit_price, discount, tax, total],
                   labels: {quantity: "Hours"}, zebra, showHeader}
    totals        {showSubtotal, showDiscount, showTax, showPaid, align}
    notes         {source: invoice|fixed, text, heading}
    terms         {text, heading}
    custom_fields {entity: invoices|customers|jobs, keys: [], heading}   empty keys = all
    spacer        {height}
    footer        {text, repeatOnEveryPage, showPageNumbers}

  Reorder or omit blocks freely. Layout beyond these options is not available:
  say so rather than inventing a block type.
`.trim();
//# sourceMappingURL=templates.js.map